import {AxiosError} from 'axios';
import {DocTableRequest} from '../../components/doc-explorer/doc-explorer-data-grid/DocExplorerDataGrid';
import BASE_URL from '../../config/env';
import {
  DocColumnUpdatePayload,
  DocCompletedCheckRequest,
  DocExplorerColumnHeader,
  MiniDocListRequest,
} from '../../shared/interfaces';
import {
  BackendErrorData,
  parseApiError,
  parseApiErrorWithLocalization,
} from '../../utils/custom-errors/parseApiError';
import {apiClient} from '../apiClient';
import {
  AddDocumentCommentRequest,
  analyticsDocPayload,
  DeleteAnnotationRequest,
  DocExplorerMasterRequest,
  DocExplorerMasterResponse,
  DocumentListRequestPayload,
  DocumentListResponse,
  DocumentViewerListIdRequest,
  DocumentViewerListIdResponse,
  DocumentViewerSortConfigRequest,
  DocumentViewerSortConfigResponse,
  DocumentViewerSortRequest,
  DocumentViewerSortResponse,
  DocViewRequest,
  DocViewResponse,
  DuplicateDocumentRequest,
  GetTranslationResponse,
  ICodingFormPinParams,
  ICommentsListGridResponse,
  ICommentsViewListResponse,
  IDocumentAudioFilePathRequest,
  IDocumentAudioFilePathResponse,
  IDocumentAudioWaveExistResponse,
  IDocumentAudioWaveRequest,
  IDocumentAudioWaveZoomInOutRequest,
  IDocumentListViewPayload,
  IDocumentMediaAddEditRemarkRequest,
  IDocumentMediaAddEditRemarkResponse,
  IDocumentMediaRedactionRequest,
  IDocumentMediaRemark,
  IDocumentMediaRemarkRequest,
  IDocumentMediaStreamingRequest,
  IDocumentMediaTranscriptRequestBody,
  IDocumentMediaTranscriptRequestQueryParams,
  IDocumentRedaction,
  IDocumentViewerCodingFormListRequest,
  IDocumentViewerCodingFormListResponse,
  IDocumentViewerCodingFormRequest,
  IDocumentViewerCodingFormResponse,
  IGetAnnotationDetailsParams,
  IGetAnnotationDetailsResponse,
  IGetCodingFormList,
  IGetCodingStamp,
  IGetCommentsListGridRequestParams,
  IGetDocDetailsMetadataGridDataReq,
  IGetDocMetadataFilterOptionsList,
  IGetDownloadDocumentParams,
  IGetDownloadDocumentResponse,
  IKeywordColorListResponse,
  IKeywordHighlightListDataPayload,
  IKeywordHighlightListResponse,
  IKeywordSavePayload,
  IPrintDocumentRequest,
  IPrintDocumentResponse,
  IRenderThreadMapParams,
  ISaveCodingFormApiResponse,
  ISaveCodingFormPayload,
  ISaveCodingFormRequest,
  ISaveCodingFormSortOrderPayload,
  ISaveRedactionTags,
  ISaveToProfileRequest,
  ISaveUserCodingStamp,
  IUserRedactionTagsPayload,
  IUserRedactionTagsResponse,
  LoadViewerRequest,
  ManualSortParams,
  ManualSortResponse,
  OptimizedSortRequest,
  OptimizedSortResponse,
  ProgressBarRequest,
  RefreshAssignmentProgressRequest,
  RedactionPanelStatus,
  RemarkPayload,
  UpsertAnnotationRequest,
  ViewingSessionRequest,
  AddNavigationNotificationPayload,
  GetDocumentForTagAndFolderRequest,
  GetDocumentForTagAndFolderResponse,
  GetNavigationForMasterListIdRequest,
  GetNavigationForMasterListIdResponse,
  IGetExportMasterDocListIdRequest,
  IGetDocumentListIdForActionResponse,
  IDocumentFilesRequest,
} from '../types/document.type';

const docManagementEndpoint = (path: string) =>
  `${BASE_URL}/documentmanagement/api/v1/${path}`;

export const getCodingFormGridList = async (queryParams: string) => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`codingform/list/grid${queryParams}`)
  );
  return data;
};

export const getFilePath = async (payload: LoadViewerRequest) => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`viewer/load`),
    payload
  );
  return data;
};

export const getViewingSessionId = async (
  payload: ViewingSessionRequest | null
) => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`viewer/renderdocument`),
    payload
  );
  return data;
};

export const getTreeNodesById = async (id: number) => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`docexplorer/tree/nodes?id=${id}`)
  );
  return data;
};
export const getCodingFormMetadata = async () => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`codingform/metadata`)
  );
  return data;
};

export const deleteCodingForm = async (codingFormId: string) => {
  const {data} = await apiClient.delete(
    docManagementEndpoint(`codingform/delete?codingFormId=${codingFormId}`)
  );
  return data;
};

export const canDeleteCodingForm = async (codingFormId: string) => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`codingform/deletable?codingFormId=${codingFormId}`)
  );
  return data;
};

export const copyCodingForm = async (codingFormId: string) => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`codingform/copy?codingFormId=${codingFormId}`)
  );
  return data;
};

export const getConceptResults = async (
  docId: string,
  conceptParameter: string,
  scorePercentage: string
) => {
  const {data} = await apiClient.get(
    docManagementEndpoint(
      `documentviewer/concept/results?docId=${docId}&conceptParameters=${conceptParameter}&minConceptScorePercent=${scorePercentage}`
    )
  );
  return data;
};

export const getCodingFormDetailsforEdit = async (codingFormId: string) => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`codingform/objects?codingFormId=${codingFormId}`)
  );
  return data;
};

export const getDocMetadataFilters = async () => {
  const {data} = await apiClient.get(
    docManagementEndpoint(
      `metadatafilters/getmetadatafilter?SourceTypeCode=DocExplorer`
    )
  );
  return data;
};
export const getDocMetadataFilterOptionsList = async (
  queryParams: IGetDocMetadataFilterOptionsList
) => {
  const {FieldId, Query, PageNumber, PageSize} = queryParams;
  let params = {};
  if (Query) params = {...queryParams};
  else params = {FieldId, PageNumber, PageSize};
  const {data} = await apiClient.get(
    docManagementEndpoint(`metadatafilters/metadata-list`),
    {params}
  );
  return data;
};

export const getDocListGridData = async (payload = {}) => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`document/list/grid`),
    payload
  );
  return data;
};

export const getDocumentList = async (
  payload: DocumentListRequestPayload
): Promise<DocumentListResponse> => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`document/list`),
    payload
  );
  return data as DocumentListResponse;
};

export const addNavigationNotification = async (
  payload: AddNavigationNotificationPayload
) => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`document/addnotification`),
    payload
  );
  return data;
};

export const getDocumentViewerListId = async (
  payload: DocumentViewerListIdRequest
): Promise<DocumentViewerListIdResponse> => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`documentviewer/list-id`),
    payload
  );
  return data as DocumentViewerListIdResponse;
};

export const getDocExplorerColumnConfig = async () => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`docexplorer/view/explorer`)
  );
  return data;
};

export const saveDocColumnConfig = async (
  payload: DocColumnUpdatePayload[]
) => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`docexplorer/headers`),
    payload
  );
  return data;
};

export const resetDocColumnConfig = async (
  payload: DocExplorerColumnHeader[]
) => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`docexplorer/headers/reset`),
    payload
  );
  return data;
};

export const getDocExplorerGridData = async (payload: DocTableRequest) => {
  const {data} = await apiClient.post(
    docManagementEndpoint('docexplorer/list/grid'),
    payload
  );
  return data;
};

export const getCodingFormList = async () => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`codingform/list`)
  );
  return data.map((item: IGetCodingFormList) => {
    return {
      ...item,
      codingFormId: Number(item.codingFormId),
    };
  });
};

export const saveCodingFormSortOrder = async (
  payload: ISaveCodingFormSortOrderPayload[]
) => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`codingform/sort`),
    payload
  );
  return data;
};

export const saveCodingForm = async (payload: ISaveCodingFormPayload) => {
  try {
    const {data} = await apiClient.post(
      docManagementEndpoint(`codingform/save`),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'CodingForms'
    );
  }
};

// Manage Comment Fields
export const getCommentsViewList =
  async (): Promise<ICommentsViewListResponse> => {
    try {
      const {data} = await apiClient.get<ICommentsViewListResponse>(
        docManagementEndpoint(`comments/view/list`)
      );
      return data;
    } catch (error) {
      throw parseApiErrorWithLocalization(
        error as AxiosError<BackendErrorData>,
        'commentsViewList'
      );
    }
  };

export const getCommentsListGrid = async (
  params: IGetCommentsListGridRequestParams
): Promise<ICommentsListGridResponse> => {
  try {
    const {data} = await apiClient.get<ICommentsListGridResponse>(
      docManagementEndpoint(`comments/list/grid`),
      {params}
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'commentsListGrid'
    );
  }
};

/// DoclistFilters
export const getDocListmetadataFilters = async () => {
  const {data} = await apiClient.get(
    docManagementEndpoint(
      `metadatafilters/getmetadatafilter?SourceTypeCode=DocList`
    )
  );
  return data;
};

export const getMetadataListDoclistFilters = async (queryParams: string) => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`metadatafilters/metadata-list${queryParams}`)
  );
  return data;
};

export const createMasterDocList = async (
  payload: DocExplorerMasterRequest
): Promise<DocExplorerMasterResponse> => {
  const {data} = await apiClient.post(
    docManagementEndpoint('docexplorer/master-doclist-docexplorer'),
    payload
  );
  return data;
};

export const createDocView = async (
  payload: DocViewRequest
): Promise<DocViewResponse> => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/docview'),
    payload
  );
  return data;
};

export const getMiniDocListGridData = async (payload: MiniDocListRequest) => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/minidoc/list'),
    payload
  );
  return data;
};
export const postDocumentViewerSort = async (
  payload: DocumentViewerSortRequest
): Promise<DocumentViewerSortResponse> => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/sort'),
    payload
  );
  return data as DocumentViewerSortResponse;
};

export const postDocumentViewerOptimizedSort = async (
  payload: OptimizedSortRequest
): Promise<OptimizedSortResponse> => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/optimized-sort'),
    payload
  );
  return data as OptimizedSortResponse;
};

export const getDocumentViewerManualSort = async (
  params: ManualSortParams
): Promise<ManualSortResponse> => {
  const searchParams = new URLSearchParams();
  params.manualCols.forEach((c) =>
    searchParams.append('manualCols', String(c))
  );
  params.manualOrder.forEach((o) =>
    searchParams.append('manualOrder', String(o))
  );
  searchParams.append(
    'configuredShowCompletedDoc',
    String(params.configuredShowCompletedDoc)
  );

  searchParams.append('sourceTypeCode', params.sourceTypeCode);
  const {data} = await apiClient.get(
    docManagementEndpoint(
      `documentviewer/manual-sort?${searchParams.toString()}`
    )
  );

  return data as ManualSortResponse;
};

export const postDocumentViewerSortConfig = async (
  payload: DocumentViewerSortConfigRequest
): Promise<DocumentViewerSortConfigResponse> => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/sort/config'),
    payload
  );
  return data as DocumentViewerSortConfigResponse;
};

export const getDocDetailsMetadataGridData = async (
  payload: IGetDocDetailsMetadataGridDataReq
) => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/metadata/list'),
    payload
  );
  return data?.getMetaData || [];
};

export const getDocListMetaData = async () => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`document/list/column-meta`)
  );
  return data;
};

export const getFolderListView = async (docId: number) => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`documentviewer/folder/list?docId=${docId}`)
  );
  return data;
};

export const getHistoryListDocdetails = async (queryParams: string) => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`documentviewer/history/list${queryParams}`)
  );
  return data;
};

export const getAnnotationListGrid = async (payload = {}) => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`annotations/all-annotations`),
    payload
  );
  return data;
};

export const deleteAnnotation = async (payload: DeleteAnnotationRequest) => {
  const {data} = await apiClient.delete(
    docManagementEndpoint(
      `annotations/delete?annotationId=${payload.selectedAnnotationId}&securityGroupId=${payload.selectedGroupId}`
    )
  );
  return data;
};

export const deleteKeywordHightlight = async (keywordId: number) => {
  const {data} = await apiClient.delete(
    docManagementEndpoint(`keywords/delete?keywordId=${keywordId}`)
  );
  return data;
};

export const getDocIsCompleted = async (payload: DocCompletedCheckRequest) => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/complete/check'),
    payload
  );
  return data;
};

export const addDocumentNewComment = async (
  payload: AddDocumentCommentRequest
) => {
  const {data} = await apiClient.post(
    docManagementEndpoint('comments/add'),
    payload
  );
  return data;
};

export const getKeywordHighlightList = async (
  queryParams: IKeywordHighlightListDataPayload
): Promise<IKeywordHighlightListResponse> => {
  const {data} = await apiClient.get(
    docManagementEndpoint('keywords/list/grid'),
    {
      params: queryParams,
    }
  );
  return data;
};

export const deleteComments = async (commentId: string) => {
  try {
    const {data} = await apiClient.delete(
      docManagementEndpoint(`comments/delete?commentId=${commentId}`)
    );
    return data;
  } catch (error) {
    throw parseApiError(
      error as AxiosError<BackendErrorData>,
      'DocumentManagement'
    );
  }
};

export const getDuplicateDocumentsForSelectedDocumentId = async (
  payload: DuplicateDocumentRequest
) => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/duplicate/selected-document'),
    payload
  );
  return data;
};

export const getDocDownloadFormats = async (param: string) => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`documentviewer/download/${param}`)
  );
  return data;
};

export const getRedactionPanelStatus = async (
  documentId: number
): Promise<RedactionPanelStatus[]> => {
  const {data} = await apiClient.get<RedactionPanelStatus[]>(
    docManagementEndpoint('documentviewer/redaction/panel/status'),
    {
      params: {documentId},
    }
  );
  return data;
};

export const getDefaultRedactionPrompt = async () => {
  const {data} = await apiClient.get(
    docManagementEndpoint('documentviewer/redaction/prompt')
  );
  return data;
};
export const setDefaultRedactionPrompt = async (payload: boolean) => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/redaction/prompt'),
    payload,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
  return data;
};
export const upsertAnnotation = async (payload: UpsertAnnotationRequest) => {
  const {data} = await apiClient.post(
    docManagementEndpoint('annotations/add'),
    payload
  );
  return data;
};

export const getAnnotationDetails = async (
  params: IGetAnnotationDetailsParams
): Promise<IGetAnnotationDetailsResponse> => {
  try {
    const {data} = await apiClient.get<IGetAnnotationDetailsResponse>(
      docManagementEndpoint(`annotations/edit`),
      {params}
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'commentsViewList'
    );
  }
};

export const getDocumentViewerCodingFormList = async (
  payload: IDocumentViewerCodingFormListRequest
): Promise<IDocumentViewerCodingFormListResponse[]> => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/coding-form/list'),
    payload
  );
  return data;
};

export const getDocumentViewerCodingForm = async (
  payload: IDocumentViewerCodingFormRequest
): Promise<IDocumentViewerCodingFormResponse> => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/coding-form'),
    payload
  );
  return data;
};

export const getDownloadDocument = async (
  queryParams: IGetDownloadDocumentParams
): Promise<IGetDownloadDocumentResponse> => {
  // Create a new URLSearchParams object to hold the query parameters
  const params = new URLSearchParams();

  // Append the necessary parameters to the query string
  params.append('DocId', queryParams.DisplayDocumentId);
  params.append('Type', String(queryParams.Type)); // e.g., 'PDF', 'TIFF', etc.
  params.append('SeqenceNumber', String(queryParams.SeqenceNumber || '1')); // Assuming rowNumber or fallback to '1'
  params.append('DisplayDocumentId', queryParams.DocId);

  // Convert the queryParams object to a query string
  const queryString = params.toString();
  try {
    const response = await apiClient.get<globalThis.Blob>(
      `${docManagementEndpoint(`documentviewer/download`)}?${queryString}`,
      {responseType: 'blob'}
    );

    // Extract the filename from content-disposition
    const disposition = response.headers['content-disposition'];
    const match = disposition?.match(/filename\*=UTF-8''(.+)$/);
    const filename = match ? decodeURIComponent(match[1]) : 'document';

    return {
      blob: response.data,
      filename: filename,
      contentDisposition: disposition, // Optionally include the content-disposition header if needed
    };
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'docViewerPanel'
    );
  }
};

export const renderThreadMap = async (payload: IRenderThreadMapParams) => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/render-map'),
    payload
  );
  return data;
};

export const getRedactionPropagation = async (): Promise<boolean> => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`documentviewer/redaction/propagation`)
  );
  return data as boolean;
};
export const getRedactionTags = async () => {
  const {data} = await apiClient.get(
    docManagementEndpoint('documentviewer/redaction/tags')
  );
  return data;
};

export const pinCodingForm = async (queryParms: ICodingFormPinParams) => {
  const {codingFormId, assignmentID = 0, isPin} = queryParms;
  const {data} = await apiClient.post(
    docManagementEndpoint(
      `documentviewer/coding-form/pin?codingFormId=${codingFormId}&assignmentID=${assignmentID}&isPin=${isPin}`
    )
  );
  return data;
};

export const getUserRedactionTags = async (
  payload: IUserRedactionTagsPayload
): Promise<IUserRedactionTagsResponse[]> => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/redaction/user-tags'),
    payload
  );
  return data ?? [];
};

export const saveRedactionTags = async (
  payload: ISaveRedactionTags
): Promise<boolean> => {
  try {
    const {data} = await apiClient.post(
      docManagementEndpoint('documentviewer/redaction/save'),
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'CodingForms'
    );
  }
};

export const getAudioFilePath = async (
  queryParams: IDocumentAudioFilePathRequest
): Promise<IDocumentAudioFilePathResponse> => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`documentviewer/audio-file-path`),
    {
      params: queryParams,
    }
  );
  return data;
};

export const getAudioWaveExist = async (
  payload: IDocumentAudioWaveRequest
): Promise<IDocumentAudioWaveExistResponse> => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`documentviewer/audio-wave`),
    payload
  );
  return data;
};

export const getDocumentFiles = async (
  payload: IDocumentFilesRequest
): Promise<Blob> => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`document/files`),
    {params: payload, responseType: 'blob'}
  );
  return data;
};

export const getAudioWaveZoomInOut = async (
  payload: IDocumentAudioWaveZoomInOutRequest
): Promise<Blob> => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`documentviewer/zoom-in-out`),
    payload,
    {responseType: 'blob'}
  );
  return data;
};

export const getMediaForStreaming = async (
  payload: IDocumentMediaStreamingRequest
): Promise<Blob> => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`media/play`),
    payload,
    {responseType: 'blob'}
  );
  return data;
};

export const getCodingStamp = async (queryParms: IGetCodingStamp) => {
  const {sourceID, userCodingFormID} = queryParms;
  const {data} = await apiClient.get(
    docManagementEndpoint(
      `documentviewer/stamp/save?sourceID=${sourceID}&userCodingFormID=${userCodingFormID}`
    )
  );
  return data;
};

export const saveUserCodingStamp = async (payload: ISaveUserCodingStamp) => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/stamp'),
    payload
  );
  return data;
};

export const getRefreshCodingStamp = async (queryParms: IGetCodingStamp) => {
  const {sourceID, userCodingFormID} = queryParms;
  const {data} = await apiClient.get(
    docManagementEndpoint(
      `documentviewer/refresh-coding-stamp?SourceID=${sourceID}&UserCodingFormID=${userCodingFormID}`
    )
  );
  return data;
};

export const getKeywordDetails = async (queryParams: any): Promise<any> => {
  const {data} = await apiClient.get(
    docManagementEndpoint('keywords/edit'),
    {params: queryParams}
  );
  return data;
};

export const getKeywordHighlightColorList =
  async (): Promise<IKeywordColorListResponse> => {
    const {data} = await apiClient.get(
      docManagementEndpoint('keywords/add')
    );
    return data;
  };

export const addKeywordHighlight = async (payload: IKeywordSavePayload) => {
  const {data} = await apiClient.post(
    docManagementEndpoint('keywords/save'),
    payload
  );
  return data;
};

export const getAnalyticsDocuments = async (payload: analyticsDocPayload) => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`documentviewer/analytics/documents`),
    payload
  );
  return data;
};

export const getTextViewDoc = async (docId: number) => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`documentviewer/text?docId=${docId}`)
  );
  return data;
};

export const getMediaRemarks = async (
  params: IDocumentMediaRemarkRequest
): Promise<IDocumentMediaRemark[]> => {
  const {data} = await apiClient.get<IDocumentMediaRemark[]>(
    docManagementEndpoint(`documentviewer/audio/remarks`),
    {params}
  );
  return data;
};

export const addEditMediaRemarks = async (
  payload: IDocumentMediaAddEditRemarkRequest
): Promise<IDocumentMediaAddEditRemarkResponse> => {
  const {data} =
    await apiClient.post<IDocumentMediaAddEditRemarkResponse>(
      docManagementEndpoint(`documentviewer/audio/remarks`),
      payload
    );
  return data;
};

export const getRemarks = async (docId: number) => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`documentviewer/remark?documentId=${docId}`)
  );
  return data;
};

export const getEditCodingStamp = async (queryParms: IGetCodingStamp) => {
  const {sourceID, userCodingFormID} = queryParms;
  const {data} = await apiClient.get(
    docManagementEndpoint(
      `documentviewer/stamp/edit?SourceID=${sourceID}&UserCodingFormID=${userCodingFormID}`
    )
  );
  return data;
};
export const getEditCodingDetails = async (queryParams: any) => {
  const {sourceID, Code, DocumentId, CodingFormId, SourceTypeCode} =
    queryParams;
  const {data} = await apiClient.get(
    docManagementEndpoint(`documentviewer/stamp/details?Code=${Code}&DocumentId=${DocumentId}&
SourceID=${sourceID}&CodingFormId=${CodingFormId}&SourceTypeCode=${SourceTypeCode}`)
  );
  return data;
};

export const addRemarks = async (payload: RemarkPayload) => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`documentviewer/remark`),
    payload
  );
  return data;
};

export const deleteRemarks = async (payload: Partial<RemarkPayload>) => {
  const {data} = await apiClient.delete(
    docManagementEndpoint(`documentviewer/remark?remarkId`),
    {data: payload}
  );
  return data;
};

export const saveEditCodingStamp = async (payload: ISaveUserCodingStamp) => {
  const {data} = await apiClient.patch(
    docManagementEndpoint(`documentviewer/stamp`),
    payload
  );
  return data;
};

export const getAudioRedactions = async (
  params: IDocumentMediaRedactionRequest
): Promise<IDocumentRedaction[]> => {
  const {data} = await apiClient.get(
    docManagementEndpoint(`documentviewer/audio/redaction`),
    {
      params,
    }
  );
  return data;
};

export const addAudioRedaction = async (
  payload: IDocumentRedaction
): Promise<any> => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`documentviewer/audio/redaction`),
    payload
  );
  return data;
};

export const deleteAudioRedaction = async (
  params: IDocumentRedaction
): Promise<any> => {
  const {data} = await apiClient.get(
    docManagementEndpoint('documentviewer/delete-audio-redaction'),
    {params}
  );
  return data;
};

export const deleteCodingStamp = async (payload: ISaveUserCodingStamp) => {
  const {data} = await apiClient.delete(
    docManagementEndpoint(`documentviewer/stamp`),
    {data: payload}
  );
  return data;
};

export const getTranslations = async (
  documentId: number,
  type: string
): Promise<GetTranslationResponse> => {
  const {data} = await apiClient.get<GetTranslationResponse>(
    docManagementEndpoint(
      `documentviewer/translations?docId=${documentId}&type=${type}`
    )
  );
  return data;
};

export const postPrintDocument = async (
  payload: IPrintDocumentRequest
): Promise<IPrintDocumentResponse> => {
  try {
    const response = await apiClient.post(
      docManagementEndpoint('documentviewer/print'),
      payload
    );
    return {status: response.status};
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'docViewerPanel'
    );
  }
};

export const saveCodingFormData = async (
  payload: ISaveCodingFormRequest
): Promise<ISaveCodingFormApiResponse> => {
  try {
    const {data} = await apiClient.post(
      docManagementEndpoint('documentviewer/coding'),
      payload
    );
    return data as ISaveCodingFormApiResponse;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'codingFormSave'
    );
  }
};

export const getMediaTranscript = async (
  params: IDocumentMediaTranscriptRequestQueryParams,
  requestBody: IDocumentMediaTranscriptRequestBody
): Promise<string> => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`documentviewer/audio/transcript`),
    requestBody,
    {params}
  );
  return data;
};

export const getDownloadProductionPDF = async (
  queryParams: IGetDownloadDocumentParams
): Promise<IGetDownloadDocumentResponse> => {
  // Create a new URLSearchParams object to hold the query parameters
  const params = new URLSearchParams();

  // Append the necessary parameters to the query string
  params.append('DocumentId', queryParams.DocId);
  params.append('DocumentFileTypeCode', String(queryParams.Type));
  params.append('SeqNo', String(queryParams.SeqenceNumber || '1'));
  params.append('ContextType', String(queryParams.ContextType || 'TIFF'));
  params.append('ProductionID', String(queryParams.ContextId || '1'));

  // Convert the queryParams object to a query string
  const queryString = params.toString();
  try {
    const response = await apiClient.get<globalThis.Blob>(
      `${docManagementEndpoint(`documentviewer/download/production-pdf`)}?${queryString}`,
      {responseType: 'blob'}
    );

    // Extract the filename from content-disposition
    const disposition = response.headers['content-disposition'];
    const match = disposition?.match(/filename\*=UTF-8''(.+)$/);
    const filename = match ? decodeURIComponent(match[1]) : 'document';

    return {
      blob: response.data,
      filename: filename,
      contentDisposition: disposition, // Optionally include the content-disposition header if needed
    };
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'docViewerPanel'
    );
  }
};

export const saveToProfile = async (payload: ISaveToProfileRequest) => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`document/doclist/profile`),
    payload
  );
  return data;
};

export const getDocumentListView = async (
  payload: IDocumentListViewPayload
) => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`document/list/view`),
    payload
  );
  return data;
};

export const codeSameAsPrevious = async (payload: any): Promise<any> => {
  try {
    const {data} = await apiClient.post(
      docManagementEndpoint('documentviewer/codesame/last'),
      payload
    );
    return data as ISaveCodingFormApiResponse;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'codingFormSave'
    );
  }
};

export const getProgressBar = async (
  payload: ProgressBarRequest
): Promise<number> => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`documentviewer/doc-list`),
    payload
  );
  return data as number;
};

export const exportDocumentHistory = async (
  documentId: number | string
): Promise<IPrintDocumentResponse> => {
  try {
    const response = await apiClient.get(
      docManagementEndpoint(
        `documentviewer/history/export?documentId=${documentId}`
      )
    );
    return {status: response.status};
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'docViewerPanel'
    );
  }
};

export const getRefreshAssignmentProgress = async (
  payload: RefreshAssignmentProgressRequest
): Promise<number> => {
  const {data} = await apiClient.get(
    docManagementEndpoint(
      `documentviewer/assignment/progress?sourceID=${payload.sourceID}`
    )
  );
  return Number(data.progressRate || 0);
};

export const getDocumentForTagAndFolder = async (
  payload: GetDocumentForTagAndFolderRequest
): Promise<GetDocumentForTagAndFolderResponse> => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`document/list/tag-folder`),
    payload
  );
  return data;
};

export const getNavigationMasterListId = async (
  payload: GetNavigationForMasterListIdRequest
): Promise<GetNavigationForMasterListIdResponse> => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`document/navigation-master-list`),
    payload
  );
  return data;
};

export const getRowNumberForDocument = async (payload: any) => {
  const {data} = await apiClient.post(
    docManagementEndpoint('documentviewer/row-number'),
    payload
  );
  return data;
};

export const getDocumentListIdForAction = async (
  payload: IGetExportMasterDocListIdRequest
): Promise<IGetDocumentListIdForActionResponse> => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`documentviewer/list-id/action`),
    payload
  );
  return data;
};
